import { useAuth0 } from '@auth0/auth0-react';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Box,
    Grid,
    LinearProgress,
    Modal,
    styled,
    TextField,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import InputImageModal from '../components/BasicComponents/InputImageModal';
import LimitedTextField from '../components/BasicComponents/LimitedTextField';
import MobileEntry from '../components/BasicComponents/MobileEntry';
import LocationSearchInput from '../components/GoogleAutoComplete';
import Title from '../components/Navbar/Title';
import sendNotification from '../utils/sendNotification';
import theme from '../muiTheme';
import { addSchool } from '../redux/TempSchoolSlice';
import titleCase from '../utils/titleCase';

const Image = styled('img')(({ theme }) => ({
    borderRadius: '15px',
    boxShadow: '0 0 25px 0 #b3bac7',
    width: 150,
    height: 150,
    margin: 'auto',
}));

const GeneralText = styled(TextField)(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginLeft: '0.5%',
    marginRight: '0.5%',
    width: '97%',
}));

const BookingButton = styled(LoadingButton)(({ theme }) => ({
    padding: '10px 0px',
    margin: '10px auto',
    width: '100%',
    maxWidth: '150px',
    borderRadius: '30px',
}));

/**
 * Component for creating a new school profile banner.
 *
 * @component AddSchool
 * @return (
 * <AddSchool/>
 * )
 */

export const AddSchool = () => {
    const { getAccessTokenSilently } = useAuth0();

    const dispatch = useDispatch();

    // profileImg Modal; default closed
    const [open, setOpen] = React.useState(false);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [state, setState] = useState({
        schoolName: {
            value: '',
            touched: false,
            error: false,
            errorCode: '',
        },
        schoolDescription: {
            value: '',
            touched: false,
            error: false,
            errorCode: '',
        },
        address: {
            value: {
                address: '',
            },
            touched: false,
            error: false,
            errorCode: '',
        },
        phoneNumber: {
            value: '',
            touched: false,
            error: false,
            errorCode: '',
        },
        fullTimeTeachers: {
            value: '0',
            touched: false,
            error: false,
            errorCode: '',
        },
        casualTeachers: {
            value: '0',
            touched: false,
            error: false,
            errorCode: '',
        },
        apiMessage: '',
        imageURL: {
            value: 'https://airteachr-static-resource.s3.ap-southeast-2.amazonaws.com/placeholder-school-image.jpeg',
            touched: false,
            error: false,
            errorCode: '',
        },
        submitError: false,
        email: '',
        ackronym: '',
        loadingImage: false,
    });

    const updateField = event => {
        const { id, value } = event.target;
        var itemStruct = {
            value: value,
            touched: true,
            error: state.error,
            errorCode: state.errorCode,
        };
        setState({
            ...state,
            [id]: itemStruct,
        });
    };

    const exitField = event => {
        const { id, value } = event.target;
        // save field values
        console.log(value);
        var itemStruct = {
            value: state[id].value,
            touched: true,
            error: false,
            errorCode: '',
        };
        if (state[id].value === '') {
            itemStruct.error = true;
            itemStruct.errorCode = 'Please input your ' + titleCase(id);
        }
        console.log(event.target);
        setState({
            ...state,
            [id]: itemStruct,
        });
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    /**
     * <function changingAddress>
     * @param   {string} value <The value returned from the address field to implement state of value>
     */
    const changingAddress = value => {
        let addressObj = state.address;
        addressObj.value = { address: value };
        addressObj.touched = true;
        setState({
            ...state,
            address: addressObj,
        });
    };

    /**
     * <function handleSelect>
     * @param {Object} addressResult Address Object
     * @param {string} addressResult.address Physical Address String
     * @param {Object} addressResult.LatLng LatLong Constructor
     * @param {string} addressResult.LatLng.Lat Latitute Definition
     * @param {string} addressResult.LatLng.Lng Longitude Definition
     *
     * @return {void} Nothing
     */

    const handleSelect = addressResult => {
        let addressObject = state.address;
        addressObject.value = {
            address: addressResult.address,
            LatLng: addressResult.LatLng,
        };
        addressObject.touched = true;
        addressObject.error = false;
        setState({
            ...state,
            address: addressObject,
        });
    };

    /**
     * <function phoneError>
     * @param {string} value Phone Number Error
     * @return {void} Nothing
     */

    function phoneError(value) {
        var interimState = state.phoneNumber;
        interimState.error = value;
        setState({
            ...state,
            phoneNumber: interimState,
        });
    }

    /**
     * <function phoneValue>
     * @param {string} value Phone Number Error
     * @return {void} Nothing
     */
    function phoneValue(value) {
        var interimState = state.phoneNumber;
        interimState.value = value;
        setState({
            ...state,
            phoneNumber: interimState,
        });
    }

    function phoneTouched(value) {
        var interimState = state.phoneNumber;
        interimState.touched = value;
        setState({
            ...state,
            phoneNumber: interimState,
        });
    }

    const createSchoolProfile = async () => {
        try {
            setLoading(true);
            async function update() {
                const token = await getAccessTokenSilently({
                    scope: process.env.REACT_APP_scope,
                });
                let dynamoData = {
                    token: token,
                    request: {
                        'displayName': state.schoolName.value,
                        'phoneNumber': state.phoneNumber.value
                            .replace(/\s/g, '')
                            .replace('+61', '0'),
                        'description': state.schoolDescription.value,
                        'address': state.address.value.address,
                        'lat': state.address.value.LatLng.lat.toString(),
                        'lng': state.address.value.LatLng.lng.toString(),
                        'schoolLogo': state.imageURL.value,
                        'casualTeachers': state.casualTeachers.value,
                        'fullTimeTeachers': state.fullTimeTeachers.value,
                    },
                };

                const SchoolResponse = await dispatch(addSchool(dynamoData));

                console.log('SchoolResponse', JSON.stringify(SchoolResponse));
                if (SchoolResponse.payload.Message.Success === true) {
                    //store the new id in redux for the other page
                    sendNotification(
                        'success',
                        6000,
                        'School information created'
                    );
                    setSubmitted(true);
                } else {
                    sendNotification(
                        'error',
                        0,
                        'Error creating school information'
                    );
                }

                setLoading(false);
            }
            update();
        } catch (err) {
            console.log('Error creating school profile');
            setState({
                ...state,
                apiMessage: 'Error creating school profile',
            });
            setLoading(false);
        }
    };

    function findError() {
        console.log('starting to look for errors');
        var addressError = { error: false, errorText: '' };
        var schoolNameError = { error: false, errorText: '' };
        var imageError = { error: false, errorText: '' };
        var phoneError = { error: false, errorText: '' };
        var descriptionError = { error: false, errorText: '' };
        var casualTeachersError = { error: false, errorText: '' };
        var fullTimeTeachersError = { error: false, errorText: '' };

        if (typeof state.address.value.LatLng === 'undefined') {
            addressError.error = true;
            addressError.errorText = 'Enter your address';
        }
        if (
            state.schoolName.value === '' ||
            state.schoolName.touched === false
        ) {
            schoolNameError.error = true;
            schoolNameError.errorText = 'Enter your school name';
        }
        if (state.imageURL.touched === false) {
            imageError.error = true;
            imageError.errorText = 'Add your School Logo';
        }
        if (state.phoneNumber.value === '') {
            phoneError.error = true;
            phoneError.errorText = 'Enter a valid Mobile Number';
        }
        if (state.schoolDescription.value === '') {
            descriptionError.error = true;
            descriptionError.errorText = 'Enter information about your school';
        }
        if (isNaN(state.casualTeachers.value)) {
            casualTeachersError.error = true;
            casualTeachersError.errorText =
                'Enter a number for the casual vacancies';
        }
        if (isNaN(state.fullTimeTeachers.value)) {
            fullTimeTeachersError.error = true;
            fullTimeTeachersError.errorText =
                'Enter a number for the full time vacancies';
        }

        setState({
            ...state,
            address: {
                value: {
                    address: state.address.value.address,
                    LatLng: state.address.value.LatLng,
                },
                touched: state.address.touched,
                error: addressError.error,
                errorCode: addressError.errorText,
            },
            schoolName: {
                value: state.schoolName.value,
                touched: state.schoolName.touched,
                error: schoolNameError.error,
                errorCode: schoolNameError.errorText,
            },
            imageURL: {
                value: state.imageURL.value,
                touched: state.imageURL.touched,
                error: imageError.error,
                errorCode: imageError.errorText,
            },
            phoneNumber: {
                value: state.phoneNumber.value,
                touched: state.phoneNumber.touched,
                error: phoneError.error,
                errorCode: phoneError.errorText,
            },
            schoolDescription: {
                value: state.schoolDescription.value,
                touched: state.schoolDescription.touched,
                error: descriptionError.error,
                errorCode: descriptionError.errorText,
            },
            casualTeachers: {
                value: state.casualTeachers.value,
                touched: state.casualTeachers.touched,
                error: casualTeachersError.error,
                errorCode: casualTeachersError.errorText,
            },
            fullTimeTeachers: {
                value: state.fullTimeTeachers.value,
                touched: state.fullTimeTeachers.touched,
                error: fullTimeTeachersError.error,
                errorCode: fullTimeTeachersError.errorText,
            },
        });
    }

    function handleProfileSubmit(event) {
        console.log('event.target: ' + JSON.stringify(event.target.id));
        event.preventDefault();
        if (
            state.schoolName.value === '' ||
            typeof state.address.value.LatLng === 'undefined' ||
            state.schoolDescription.value === '' ||
            state.phoneNumber.value === '' ||
            state.imageURL.touched === false ||
            state.phoneNumber.error === true ||
            isNaN(state.fullTimeTeachers.value) ||
            isNaN(state.casualTeachers.value)
        ) {
            findError();
        } else {
            console.log('Starting school creation');
            createSchoolProfile();
        }
    }

    let callback = imageURL => {
        console.log('imageURL', imageURL);
        var itemStruct = {
            value: imageURL,
            touched: true,
            error: false,
            errorCode: '',
        };
        setState({
            ...state,
            imageURL: itemStruct,
        });
        handleClose();
    };

    let history = useHistory();

    return (
        <Box>
            <Title title="Add Your School"></Title>
            <Grid
                container
                xs={11}
                sx={{
                    backgroundColor: '#FFF',
                    padding: '20px',
                    borderRadius: '15px',
                    margin: 'auto',
                    marginTop: '30px',
                }}
            >
                <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{
                        margin: '30px 0px',
                        textAlign: 'center',
                    }}
                >
                    {state.imageURL.error === false ? (
                        <Image
                            src={state.imageURL.value}
                            onClick={handleOpen}
                            alt="School Logo"
                            data-cy="school-image"
                        />
                    ) : (
                        <Box>
                            <Image
                                sx={{
                                    border: '1px red solid',
                                }}
                                src={state.imageURL.value}
                                onClick={handleOpen}
                                alt="School Logo"
                            />
                            <Typography color="error">
                                Image Upload Required
                            </Typography>
                        </Box>
                    )}

                    {state.loadingImage && <LinearProgress sx={{ m: 1 }} />}
                </Grid>
                <Grid
                    item
                    md={8}
                    xs={12}
                    sx={{
                        margin: 'auto',
                        width: '100%',
                    }}
                >
                    <GeneralText
                        disabled={submitted}
                        error={state.schoolName.error}
                        id="schoolName"
                        data-cy="school-name"
                        label="School Name"
                        variant="outlined"
                        helperText={state.schoolName.errorCode}
                        value={state.schoolName.value}
                        onChange={updateField}
                        onBlur={exitField}
                    />

                    <LimitedTextField
                        disabled={submitted}
                        label="schoolDescription"
                        data-cy="school-desc"
                        error={state.schoolDescription.error}
                        updateField={updateField}
                        width="97%"
                        textLength={500}
                        value={state.schoolDescription.value}
                    />

                    <MobileEntry
                        disabled={submitted}
                        id="phone"
                        label="Mobile Number"
                        variant="outlined"
                        phoneNumber={state.phoneNumber.value}
                        error={phoneError}
                        passThroughError={state.phoneNumber.error}
                        touched={phoneTouched}
                        value={phoneValue}
                        sx={{
                            width: '97%',
                            marginTop: theme.spacing(2),
                            marginBottom: theme.spacing(2),
                        }}
                    />

                    <Box>
                        <LocationSearchInput
                            disabled={submitted}
                            id="address"
                            data-cy="address"
                            onSelect={handleSelect}
                            onChange={changingAddress}
                            error={state.address.error}
                            searchType="address"
                            initialAddress={state.address.value.address}
                            sx={{
                                width: '97%',
                                marginTop: theme.spacing(2),
                                marginBottom: theme.spacing(2),
                            }}
                        />
                    </Box>

                    <GeneralText
                        disabled={submitted}
                        error={state.fullTimeTeachers.error}
                        id="fullTimeTeachers"
                        data-cy="fullTimeTeachers"
                        label="Full Time Teacher Vacancies"
                        variant="outlined"
                        helperText={state.fullTimeTeachers.errorCode}
                        value={state.fullTimeTeachers.value}
                        onChange={updateField}
                        onBlur={exitField}
                    />

                    <GeneralText
                        disabled={submitted}
                        error={state.casualTeachers.error}
                        id="casualTeachers"
                        data-cy="casualTeachers"
                        label="Casual Teacher Vacancies"
                        variant="outlined"
                        helperText={state.casualTeachers.errorCode}
                        value={state.casualTeachers.value}
                        onChange={updateField}
                        onBlur={exitField}
                    />

                    <br />
                    <Box textAlign="center">
                        <BookingButton
                            sx={{ margin: '10px' }}
                            id="backButton"
                            data-cy="back-next"
                            size="large"
                            color="primary"
                            onClick={() => history.goBack()}
                            startIcon={!submitted && <ArrowBackIosIcon />}
                            endIcon={submitted && <ArrowForwardIosIcon />}
                            variant="outlined"
                        >
                            {submitted ? 'Next' : 'Back'}
                        </BookingButton>

                        <BookingButton
                            disabled={submitted}
                            sx={{ margin: '10px' }}
                            id="profileSave"
                            data-cy="save"
                            size="large"
                            color="primary"
                            onClick={event => {
                                handleProfileSubmit(event);
                            }}
                            loading={loading}
                            loadingPosition="start"
                            startIcon={<SaveIcon />}
                            variant="outlined"
                        >
                            Save
                        </BookingButton>
                    </Box>

                    {/* <Button
                    id="profileSave"
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={event => {
                        handleProfileSubmit(event);
                    }}
                >
                    Save
                </Button> */}
                </Grid>
                <Modal
                    open={open}
                    onClose={handleClose}
                    aria-labelledby="Modal-Profile-Image"
                    aria-describedby="Open-Modal-For-Upload"
                >
                    <InputImageModal
                        parentCallback={callback}
                        text="Profile Image"
                    />
                </Modal>
            </Grid>
        </Box>
    );
};

export default AddSchool;
